import React, { useState } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  primaryBtnModernClasses,
  secondaryBtnModernClasses,
} from "../styles/uiClasses";
import { navbarClasses } from "../styles/componentClasses";
import { SearchIcon } from "../icons/SvgIcons";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/blog");
  };

  const handleHomeNavigation = () => {
    setMenuOpen(false);

    if (location.pathname === "/blog" || location.pathname === "/") {
      window.history.replaceState(null, "", "/blog");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    navigate("/blog");
  };

  const btnPrimary = `${navbarClasses.buttonBase} ${primaryBtnModernClasses}`;
  const btnSecondary = `${navbarClasses.buttonBase} ${secondaryBtnModernClasses}`;
  const btnGhost = `${navbarClasses.buttonBase} ${navbarClasses.buttonGhost}`;

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `${navbarClasses.linkBase} ${isActive ? navbarClasses.linkActive : navbarClasses.linkInactive}`;
  const showPublicSearch = !isAdmin && location.pathname === "/blog";

  const updatePublicSearch = (nextQ: string) => {
    const params = new URLSearchParams(searchParams);
    const value = nextQ.trim();
    if (value) params.set("q", nextQ);
    else params.delete("q");
    params.delete("sort");

    setSearchParams(params, { replace: true });
  };

  return (
    <nav className={navbarClasses.wrapper}>
      <div className={navbarClasses.container}>
        <div className={navbarClasses.content}>
          <button
            onClick={handleHomeNavigation}
            className={navbarClasses.brandButton}
            aria-label="Go to home"
          >
            <span className={navbarClasses.brandTitle}>
              Summer Jobs in Greece
            </span>
            <span className={navbarClasses.brandSubtitle}>
              Stories and seasonal life across the islands
            </span>
          </button>

          {showPublicSearch ? (
            <div className={navbarClasses.publicSearchWrap}>
              <SearchIcon className={navbarClasses.publicFilterIcon} />
              <input
                value={searchParams.get("q") ?? ""}
                onChange={(e) => updatePublicSearch(e.target.value)}
                placeholder="Search articles, topics, and destinations..."
                className={navbarClasses.publicFilterInput}
                inputMode="search"
                aria-label="Search articles"
              />
            </div>
          ) : null}

          {isAdmin ? (
            <>
              <div className={navbarClasses.desktopNav}>
                <NavLink to="/blog" className={linkClass} onClick={handleHomeNavigation}>
                  Blog
                </NavLink>
                <NavLink to="/about" className={linkClass}>
                  About
                </NavLink>
                <NavLink to="/contact" className={linkClass}>
                  Contact
                </NavLink>
              </div>

              <div className={navbarClasses.desktopActions}>
                <button
                  onClick={() => navigate("/admin/blog")}
                  className={btnSecondary}
                >
                  Manage Blog
                </button>
                <button
                  onClick={() => navigate("/admin/jobs")}
                  className={btnSecondary}
                >
                  Manage Jobs
                </button>
                <button
                  onClick={() => navigate("/admin/password")}
                  className={btnSecondary}
                >
                  Change Password
                </button>
                <button onClick={handleLogout} className={btnGhost}>
                  Logout
                </button>
              </div>

              <div className={navbarClasses.mobileToggleWrap}>
                <button
                  onClick={() => setMenuOpen((s) => !s)}
                  className={navbarClasses.mobileToggle}
                  aria-label="Toggle menu"
                  aria-expanded={menuOpen}
                  aria-controls="mobile-menu"
                >
                  <svg
                    className={navbarClasses.mobileToggleIcon}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {menuOpen ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    )}
                  </svg>
                </button>
              </div>
            </>
          ) : null}
        </div>

        {showPublicSearch ? (
          <div className={navbarClasses.publicSearchMobileWrap}>
            <SearchIcon className={navbarClasses.publicFilterIcon} />
            <input
              value={searchParams.get("q") ?? ""}
              onChange={(e) => updatePublicSearch(e.target.value)}
              placeholder="Search articles, topics, and destinations..."
              className={navbarClasses.publicFilterInput}
              inputMode="search"
              aria-label="Search articles"
            />
          </div>
        ) : null}
      </div>

      {isAdmin ? (
        <div
          id="mobile-menu"
          className={`${navbarClasses.mobileMenuBase} ${
            menuOpen ? navbarClasses.mobileMenuOpen : navbarClasses.mobileMenuClosed
          }`}
        >
          <div className={navbarClasses.mobileMenuPanel}>
            <div className={navbarClasses.mobileMenuContent}>
              <div className={navbarClasses.mobileStack}>
                <NavLink
                  to="/blog"
                  className={linkClass}
                  onClick={handleHomeNavigation}
                >
                  Blog
                </NavLink>
                <NavLink
                  to="/about"
                  className={linkClass}
                  onClick={() => setMenuOpen(false)}
                >
                  About
                </NavLink>
                <NavLink
                  to="/contact"
                  className={linkClass}
                  onClick={() => setMenuOpen(false)}
                >
                  Contact
                </NavLink>

                <div className={navbarClasses.mobileDivider} />

                <button
                  onClick={() => {
                    navigate("/admin/blog");
                    setMenuOpen(false);
                  }}
                  className={btnSecondary}
                >
                  Manage Blog
                </button>
                <button
                  onClick={() => {
                    navigate("/admin/jobs");
                    setMenuOpen(false);
                  }}
                  className={btnPrimary}
                >
                  Manage Jobs
                </button>
                <button
                  onClick={() => {
                    navigate("/admin/password");
                    setMenuOpen(false);
                  }}
                  className={btnSecondary}
                >
                  Change Password
                </button>
                <button
                  onClick={() => {
                    handleLogout();
                    setMenuOpen(false);
                  }}
                  className={btnGhost}
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </nav>
  );
}
